import { MdAddCircleOutline } from "react-icons/md";
import { useEffect, useState } from "react";
import {
  Course,
  Module,
  Program,
  Project,
  QuizListType,
} from "utils/interfaces";
import QuizCheckbox from "./QuizCheckbox";
import CreateEditQuiz from "./CreateNewQuiz";
import EmptyViewPopUp from "components/main-view/utils/pop-up-panels/common/EmptyViewPopUp";
import WarningPopUpMessage from "components/main-view/utils/pop-up-panels/common/WarningPopUpMessage";
import { motion } from "framer-motion";
import { defaultBgVariants } from "../common/defaultValues";
import BackButtonHeaderPopUp from "../common/BackButtonHeader";

interface QuizPanelProps {
  type?: "quiz" | "edit-quiz";
  selectedLessonData?: Program | Course | Project | Module | null;
  selectedQuizToEdit?: QuizListType | null;
  quizzes: QuizListType[];
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  setQuizzes: React.Dispatch<React.SetStateAction<QuizListType[]>>;
}

const QuizPanel: React.FC<QuizPanelProps> = ({
  type,
  selectedLessonData,
  selectedQuizToEdit,
  quizzes,
  setShowPopUp,
  setQuizzes,
}) => {
  // States definition
  const [quizData, setQuizData] = useState<QuizListType[]>(quizzes);
  const [popUpState, setPopUpState] = useState<
    "main" | "create-new" | "add-question" | "edit-question"
  >(selectedQuizToEdit ? "create-new" : "main");
  const [activateConfirmChanges, setActivateConfirmChanges] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);

  /**
   * Handle clicking in the black screen
   */
  const handleClickBlackScreen = () => {
    // Prevent closing for these types
    if (type === "quiz") {
      // If the pop up state is one of these ones, then don't allow to
      // close when clicking the black screen
      if (
        ["create-new", "add-question", "edit-question"].includes(popUpState)
      ) {
        // Activate the warning message
        setShowWarningMessage(true);
        // And finish the function
        return;
      }
    }

    // Close the pop up
    setShowPopUp(false);
    // Force close the warning message
    setShowWarningMessage(false);
  };

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    // If create new was pressed, then come back to the regular option
    if (["add-question", "edit-question"].includes(popUpState)) {
      setPopUpState("create-new");
    } else if (popUpState === "create-new") {
      // If we are just editing, then close pop up from teacher
      if (selectedQuizToEdit) setShowPopUp(false);
      // Otherwise, just come back
      else setPopUpState("main");
    }
    // Otherwise, close the pop up
    else setShowPopUp(false);
  };

  /**
   * Add the new selected data to the current one
   */
  const handleConfirmChange = () => {
    // And set the new quizzes to state
    setQuizzes(quizData);

    // And close the pop-up
    setShowPopUp(false);
  };

  /**
   * Definition of the Add or remove action
   * @param id of the selected id in the pop up menu
   * @param action "add" or "remove" "selectedData._id" from "id"
   */
  const selectAction = (index: number, action: "add" | "remove") => {
    // Define a new version of the quizData
    const updatedQuizzes = [...quizData];

    if (action === "add") {
      // Change the "activate" state of this quiz
      updatedQuizzes[index].active = true;
    } else {
      // Change the "activate" state of this quiz
      updatedQuizzes[index].active = false;
    }

    // And updating
    setQuizData(updatedQuizzes);
  };

  /**
   * Definition of the function executed after creating a quiz
   * in the database
   */
  const afterCreateEditQuiz = () => {
    // If we are editing, come back to the regular view (depending on the justCreateNew option)
    if (selectedQuizToEdit) {
      // And close the pop-up
      setShowPopUp(false);
    } else {
      // And come back to the main view
      setPopUpState("main");
    }
  };

  /**
   * Timer for the warning pop up message
   */
  useEffect(() => {
    if (showWarningMessage) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        if (setShowWarningMessage) setShowWarningMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showWarningMessage]);

  /**
   * Update data when quizzes is changed
   */
  useEffect(() => {
    setQuizData(quizzes);
  }, [quizzes]);

  return (
    <motion.div
      className="fixed inset-0 inline-flex items-end p-5 justify-center z-30"
      variants={defaultBgVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1, ease: "easeOut" }}
    >
      <div
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        onClick={handleClickBlackScreen}
      />
      {/* Needed to encapsulate this to ensure `react-beautiful-dnd` works alright inside this module */}
      <div className="w-fit h-fit px-6 pt-6 pb-8 bg-white rounded-3xl flex-col gap-4 justify-between items-start inline-flex overflow-hidden z-40">
        <div className="relative w-fit h-fit max-h-[80vh] flex-col justify-start items-start gap-6 inline-flex">
          <div className="self-stretch justify-between items-center inline-flex">
            <div className="justify-start items-center gap-3 flex">
              <BackButtonHeaderPopUp onClick={handlePressBack} />
              <div className="text-neutral-600 text-lg font-normal font-poppins leading-snug">
                {popUpState === "main"
                  ? "Add/Remove Quizzes"
                  : popUpState === "create-new"
                    ? selectedQuizToEdit
                      ? "Edit Quiz"
                      : "Create new Quiz"
                    : popUpState === "add-question"
                      ? "Add Question"
                      : "Edit Question"}
              </div>
            </div>
            {/* Hide create new button when creating parent */}
            {popUpState === "main" && (
              <div
                className="group rounded justify-center items-center gap-2 flex cursor-pointer"
                onClick={() => setPopUpState("create-new")}
              >
                <div className="w-6 h-6 relative">
                  <MdAddCircleOutline className="w-full h-full fill-secondary-600 group-hover:fill-secondary-700" />
                </div>
                <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                  Create new
                </div>
              </div>
            )}
          </div>
          {popUpState === "main" && (
            <>
              <div className="w-[414px] self-stretch justify-start items-start gap-[5px] inline-flex overflow-y-auto pop-up-scrollbar">
                <div className="w-full flex-col justify-start items-start gap-4 inline-flex">
                  {quizData.length > 0 ? (
                    quizData.map((quiz, index) => (
                      <QuizCheckbox
                        key={index}
                        index={index}
                        quiz={quiz}
                        selectAction={selectAction}
                        setActivateConfirmChanges={setActivateConfirmChanges}
                      />
                    ))
                  ) : (
                    <EmptyViewPopUp />
                  )}
                </div>
              </div>
              {quizData.length > 0 && (
                <>
                  {activateConfirmChanges ? (
                    <div
                      className="self-stretch px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
                      onClick={handleConfirmChange}
                    >
                      <div className="text-white text-lg font-semibold font-poppins leading-snug select-none">
                        Confirm Changes
                      </div>
                    </div>
                  ) : (
                    <div className="self-stretch px-5 py-3 bg-Subtle rounded justify-center items-center gap-2 inline-flex cursor-not-allowed">
                      <div className="text-neutral-400 text-lg font-semibold font-poppins leading-snug select-none">
                        Confirm Changes
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {popUpState !== "main" && (
            <CreateEditQuiz
              quizzes={quizzes}
              selectedLessonData={selectedLessonData}
              selectedQuizToEdit={selectedQuizToEdit}
              popUpState={popUpState}
              setPopUpState={setPopUpState}
              setQuizzes={setQuizzes}
              afterCreateEditFunction={afterCreateEditQuiz}
              handlePressBack={handlePressBack}
            />
          )}
          {setShowWarningMessage && (
            <WarningPopUpMessage
              showWarningMessage={showWarningMessage}
              setShowWarningMessage={setShowWarningMessage}
              addBreaks={false}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
};
export default QuizPanel;
